import React, { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { Tag, useAssignContentsTagsMutation, useGetTagListQuery } from '../../../generated/graphql';
import { FormMultiSelect, FormCheckbox } from '../../form';
import styles from './AddContentsTagsDialog.module.css';

type AddContentsTagsDialogProps = {
    onClose: () => void,
    libraryId: string,
    ids: string[],
    visible: boolean,
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const AddContentsTagsDialog = (props: AddContentsTagsDialogProps) => {
    const { libraryId, ids, style, visible, onClose } = props;
    const [assignContentsTags, assignContentsTagsResult] = useAssignContentsTagsMutation();
    const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
    const [overwrite, setOverwrite] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [isLoadingAllTags, setIsLoadingAllTags] = useState(true);
    const fetchedCursors = useRef(new Set<string>());
    const tags = useGetTagListQuery({
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true,
        variables: { contains: "" }
    });
    const { data: tagData, error: tagError, fetchMore: fetchMoreTags } = tags;

    useEffect(() => {
        const pageInfo = tagData?.TagList?.pageInfo;

        if (tagError) {
            setIsLoadingAllTags(false);
            setValidationError('Unable to load the available tags.');
            return;
        }

        if (!pageInfo) return;
        if (!pageInfo.hasNextPage || !pageInfo.endCursor) {
            setIsLoadingAllTags(false);
            return;
        }

        const cursor = pageInfo.endCursor;
        if (fetchedCursors.current.has(cursor)) return;
        fetchedCursors.current.add(cursor);
        setIsLoadingAllTags(true);

        fetchMoreTags({
            variables: { contains: "", after: cursor },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                if (!fetchMoreResult?.TagList) return previousResult;

                const existingNodes = previousResult.TagList?.nodes || [];
                const existingIds = new Set(existingNodes.map(tag => tag?.id).filter(Boolean));
                const newNodes = (fetchMoreResult.TagList.nodes || [])
                    .filter(tag => !tag?.id || !existingIds.has(tag.id));

                return {
                    ...previousResult,
                    TagList: {
                        ...fetchMoreResult.TagList,
                        nodes: [...existingNodes, ...newNodes],
                    },
                };
            },
        }).catch(() => {
            setIsLoadingAllTags(false);
            setValidationError('Unable to load all available tags.');
        });
    }, [tagData?.TagList?.pageInfo, tagError, fetchMoreTags]);

    const sortedTags = [...(tagData?.TagList?.nodes || [])].sort((a, b) =>
        (a?.fullName || "") < (b?.fullName || "") ? -1 : (a?.fullName || "") > (b?.fullName || "") ? 1 : 0
    );

    const handleSubmit = () => {
        if (selectedTags.length === 0) {
            setValidationError('Please fill all of the following fields.');
            return;
        }
        setValidationError('');
        const tagIds = selectedTags.map(t => t.id || "");
        assignContentsTags({
            variables: { libraryId, ids, tagIds, overwrite }
        })
            .then(() => { onClose(); })
            .catch(() => {});
    };

    return (
        <DialogBase
            title="Assign Content Tags"
            type="OkCancel"
            isLoading={assignContentsTagsResult.loading || isLoadingAllTags}
            visible={visible}
            onClose={onClose}
            onSubmit={handleSubmit}
            validationError={validationError}
            className={styles.AddContentsTagsDialog}
            style={style}
            data-testid="AddContentsTagsDialog">
            <FormMultiSelect
                label="Tags"
                name="tags"
                value={selectedTags}
                onChange={(v) => setSelectedTags(v as Tag[])}
                options={sortedTags}
                textField="fullName"
            />
            <FormCheckbox
                label="Overwrite"
                name="overwrite"
                checked={overwrite}
                onChange={setOverwrite}
            />
        </DialogBase>
    );
};

export default AddContentsTagsDialog;
